import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { MapPin, Leaf, ShieldCheck, Heart, Flame, Package, Users, ArrowRight } from "lucide-react";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.75, ease: [0.16, 1, 0.3, 1], delay },
});

const Label = ({ children }) => (
  <span className="inline-block text-xs font-medium text-earth-green uppercase tracking-widest mb-4">
    {children}
  </span>
);

const Divider = () => (
  <div className="w-12 h-px bg-earth-green/30 my-6" />
);

/* ─── 1. HERO ─────────────────────────────────────────────────────────────── */
const AboutHero = () => (
  <section className="relative pt-36 pb-24 lg:pt-44 lg:pb-32 bg-cream overflow-hidden">
    <div className="absolute inset-0 pointer-events-none">
      <img
        src={`${process.env.PUBLIC_URL}/about-bg.png`}
        alt=""
        className="absolute inset-0 h-full w-full object-cover object-top opacity-80"
      />
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(247,241,232,0.2)_0%,rgba(247,241,232,0.72)_40%,rgba(247,241,232,0.96)_72%,#f7f1e8_100%)]" />
      <div className="absolute top-0 right-0 w-[500px] h-[500px] spice-dust opacity-25" />
    </div>
    <div className="section-container relative z-10 max-w-4xl">
      <motion.div {...fadeUp(0)}>
        <Label>Our Story</Label>
      </motion.div>
      <motion.h1
        {...fadeUp(0.1)}
        className="font-accent italic text-5xl sm:text-6xl lg:text-7xl text-sw-text leading-[1.1] mb-8"
      >
        Food has always<br />
        <span className="text-earth-green">been personal.</span>
      </motion.h1>
      <motion.p
        {...fadeUp(0.2)}
        className="text-lg lg:text-xl text-sw-text-secondary max-w-2xl leading-relaxed"
      >
        Swadishes is a food brand built on a single, honest belief: that traditional food
        has never been one-size-fits-all. Every home adjusts. Every hand that cooks
        knows to dial something up or hold something back. We simply built a way to do
        that consciously, consistently, and with full transparency.
      </motion.p>
      <motion.div {...fadeUp(0.3)} className="flex flex-wrap gap-3 mt-10">
        {[
          { icon: MapPin, text: "Bhavapuri, Andhra Pradesh" },
          { icon: MapPin, text: "Amravathi, Maharashtra" },
        ].map(({ icon: Icon, text }) => (
          <div key={text} className="flex items-center gap-2 bg-white border border-gray-100 rounded-full px-4 py-2 shadow-sm">
            <Icon size={13} className="text-earth-green" />
            <span className="text-xs font-medium text-sw-text-secondary">{text}</span>
          </div>
        ))}
      </motion.div>
    </div>
  </section>
);

/* ─── 2. BRAND ESSENCE ────────────────────────────────────────────────────── */
const BrandEssence = () => (
  <section className="py-24 lg:py-36 bg-earth-green">
    <div className="section-container">
      <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
        <motion.div {...fadeUp(0)}>
          <p className="font-accent italic text-3xl lg:text-4xl text-cream/90 leading-[1.4]">
            "Every mother who cooked for her family already understood personalisation.
            She just never called it that."
          </p>
          <Divider />
          <p className="text-cream/60 text-sm font-medium">The instinct behind Swadishes</p>
        </motion.div>
        <motion.div {...fadeUp(0.15)} className="space-y-6">
          <p className="text-cream/80 text-base lg:text-lg leading-relaxed">
            Traditional Indian food, especially pickles and podis, has always carried
            a fingerprint. Your grandmother's avakaya was not the same as her neighbour's.
            Your mother's rasam powder tasted like home precisely because of the adjustments
            she made without thinking about it.
          </p>
          <p className="text-cream/80 text-base lg:text-lg leading-relaxed">
            Swadishes is an attempt to honour that. Not to industrialise it, not to flatten
            it. We simply want to make the same level of care and personalisation available to people
            who may not have the time, the knowledge, or the access to do it themselves.
          </p>
          <p className="text-cream/80 text-base lg:text-lg leading-relaxed">
            We are a small, deliberate brand. We exist to be careful, not to be big.
          </p>
        </motion.div>
      </div>
    </div>
  </section>
);

/* ─── 3. WHERE WE COME FROM ───────────────────────────────────────────────── */
const RegionCard = ({ delay, accent, label, place, heading, body1, body2 }) => (
  <motion.div
    {...fadeUp(delay)}
    className="relative rounded-3xl overflow-hidden p-10 lg:p-12"
    style={{ backgroundColor: accent }}
  >
    <div className="absolute top-0 right-0 w-48 h-48 rounded-full opacity-10 -translate-y-1/2 translate-x-1/2"
      style={{ backgroundColor: "#fff" }} />
    <span className="text-[11px] font-semibold uppercase tracking-widest text-white/60 mb-2 block">{label}</span>
    <h3 className="font-heading font-bold text-2xl lg:text-3xl text-white mb-1">{place}</h3>
    <div className="w-8 h-px bg-white/30 my-5" />
    <p className="text-white/80 text-base leading-relaxed mb-4">{body1}</p>
    <p className="text-white/65 text-sm leading-relaxed">{body2}</p>
  </motion.div>
);

const WhereWeFrom = () => (
  <section className="relative py-24 lg:py-36 bg-cream-secondary/40 overflow-hidden">
    <div className="absolute inset-0 pointer-events-none">
      <img
        src={`${process.env.PUBLIC_URL}/cities-bg.png`}
        alt=""
        className="absolute inset-0 h-full w-full object-cover object-center opacity-75"
      />
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(248,244,237,0.88)_0%,rgba(248,244,237,0.76)_32%,rgba(248,244,237,0.9)_100%)]" />
    </div>
    <div className="section-container relative z-10">
      <motion.div {...fadeUp(0)} className="max-w-2xl mb-16">
        <Label>Roots</Label>
        <h2 className="font-heading font-bold text-3xl lg:text-5xl text-sw-text leading-tight mb-6">
          Two places.<br />
          <span className="font-accent italic text-earth-green">One food memory.</span>
        </h2>
        <p className="text-sw-text-secondary text-base lg:text-lg leading-relaxed">
          Swadishes does not come from a boardroom or a market-research brief.
          It comes from two real places, connected by food, by family, and by a
          shared respect for what a well-made pickle or podi actually means.
        </p>
      </motion.div>

      <div className="grid lg:grid-cols-2 gap-6">
        <RegionCard
          delay={0.1}
          accent="#4A5D23"
          label="Where our roots grow"
          place="Bhavapuri (Bapatla), Andhra Pradesh"
          body1="Andhra Pradesh is where the boldest pickle traditions in India live. The red chilli culture, the mustard-and-sesame-oil foundations, the raw mango season that families plan around. This is the culinary DNA of Swadishes."
          body2="Bhavapuri is not an abstraction for us. It is where our ingredient relationships, our spice sourcing, and our recipe heritage are anchored. The pickles we make carry Andhra's directness. They are not shy, not diluted, not designed to be inoffensive. They are honest, bold, and made to be eaten."
        />
        <RegionCard
          delay={0.2}
          accent="#9F3E18"
          label="Where our heart also belongs"
          place="Amravathi, Maharashtra"
          body1="Maharashtra is not a secondary location for Swadishes. It is where another part of the brand's soul lives: the cross-regional understanding, the care for households that eat differently, the families that bridge south and west in their kitchens every single day."
          body2="Amravathi represents that bridge. It is where Swadishes understands that food personalisation is not just a preference. It is sometimes a necessity. For the diabetic grandfather, the child who cannot handle heat, the pregnant woman navigating restrictions with no compromise on taste. Maharashtra shaped that empathy in us."
        />
      </div>
    </div>
  </section>
);

/* ─── 4. WHO MAKES SWADISHES ──────────────────────────────────────────────── */
const KitchenPillars = [
  {
    icon: Heart,
    title: "Patience over speed",
    desc: "Pickling is not a process that rewards rushing. Our kitchen teams understand that curing, balancing, and resting a pickle is as important as the ingredients themselves.",
  },
  {
    icon: Users,
    title: "Generational knowledge",
    desc: "The women in our kitchens carry recipes and techniques that were never written down. They exist in memory, in hand-feel, in instinct. That is irreplaceable and we treat it as such.",
  },
  {
    icon: Flame,
    title: "Spice intuition",
    desc: "Understanding heat is not just about measurement. It is about knowing how a chilli roasts, how a podi changes character with time, how oil and spice settle together. Our team brings that lived understanding.",
  },
  {
    icon: Leaf,
    title: "Balance as philosophy",
    desc: "Salt, sourness, oil, heat: these are not just settings. They are in conversation with each other in every recipe. Getting the balance right is the craft, and our kitchen teams approach it that way.",
  },
];

const WhoMakes = () => (
  <section className="py-24 lg:py-36 bg-cream">
    <div className="section-container">
      <div className="grid lg:grid-cols-2 gap-16 items-start">
        <motion.div {...fadeUp(0)} className="lg:sticky lg:top-28">
          <Label>The hands behind every jar</Label>
          <h2 className="font-heading font-bold text-3xl lg:text-5xl text-sw-text leading-tight mb-6">
            Swadishes is shaped by people who cook<br />
            <span className="font-accent italic text-earth-green">from memory.</span>
          </h2>
          <p className="text-sw-text-secondary text-base lg:text-lg leading-relaxed mb-6">
            Our kitchen is led by women: experienced home cooks, mothers, and people
            who have spent decades understanding how food works. Not in a laboratory.
            In real kitchens, for real families, through real seasons.
          </p>
          <p className="text-sw-text-secondary text-base leading-relaxed mb-6">
            We do not use the word "artisan" lightly. But there is no more accurate
            word for someone who knows, by the sound of a tempering, whether the
            mustard seeds are ready. Or who adjusts the tang of a batch by taste,
            not by formula.
          </p>
          <p className="text-sw-text-secondary text-base leading-relaxed">
            These are the people who prepare your order. We think you should know that.
          </p>
        </motion.div>

        <div className="space-y-5">
          {KitchenPillars.map((item, i) => (
            <motion.div
              key={item.title}
              {...fadeUp(i * 0.1)}
              className="bg-cream-secondary/60 border border-cream-tertiary rounded-2xl p-7"
            >
              <div className="flex items-start gap-5">
                <div className="w-10 h-10 rounded-xl bg-earth-green/10 flex items-center justify-center shrink-0 mt-0.5">
                  <item.icon size={18} className="text-earth-green" />
                </div>
                <div>
                  <h4 className="font-heading font-semibold text-sw-text mb-2">{item.title}</h4>
                  <p className="text-sw-text-secondary text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ─── 5. HOW WE PREPARE ───────────────────────────────────────────────────── */
const PrepSteps = [
  {
    num: "01",
    title: "Ingredient selection",
    body: "Every batch begins with ingredient evaluation, not just sourcing. Raw mangoes are assessed for ripeness and acidity. Chillies are selected for character, not just heat level. Sesame oil is chosen for aroma and purity. Nothing enters the kitchen as an afterthought.",
  },
  {
    num: "02",
    title: "Spice preparation",
    body: "Spices are roasted, ground, and blended in-house. We do not use pre-ground mixes. The difference in aroma between freshly roasted fenugreek and a pre-ground commercial blend is not subtle. It is the difference between a living recipe and a manufactured one.",
  },
  {
    num: "03",
    title: "Recipe preparation & curing",
    body: "Pickles are not assembled. They are constructed in a sequence that matters. Oil ratios, salt timing, spice layering, and curing duration all follow method developed through practice and refinement. Each batch is given the time it needs.",
  },
  {
    num: "04",
    title: "Balance & finishing",
    body: "Before a batch is approved, it goes through a tasting and balance check. Is the heat sitting correctly? Is the sourness integrated? Is the oil balanced without overwhelming? This is where the experience of our kitchen team is most visible.",
  },
  {
    num: "05",
    title: "Careful packaging",
    body: "Packaging is done under controlled conditions. Jars are filled, sealed, labelled with full ingredient and profile information, and checked before dispatch. We take packaging seriously because everything we put into the preparation deserves to arrive intact.",
  },
];

const HowWePrepare = () => (
  <section className="py-24 lg:py-36 bg-cream-secondary/50">
    <div className="section-container">
      <motion.div {...fadeUp(0)} className="max-w-2xl mb-16">
        <Label>Preparation</Label>
        <h2 className="font-heading font-bold text-3xl lg:text-5xl text-sw-text leading-tight mb-6">
          What goes into<br />
          <span className="font-accent italic text-earth-green">every jar.</span>
        </h2>
        <p className="text-sw-text-secondary text-base lg:text-lg leading-relaxed">
          We want to be transparent about how Swadishes products are made, not as a
          marketing exercise, but because we believe you should know exactly what you
          are eating and how it was prepared.
        </p>
      </motion.div>

      <div className="relative">
        <div className="hidden lg:block absolute left-[26px] top-8 bottom-8 w-px bg-earth-green/15" />
        <div className="space-y-8">
          {PrepSteps.map((step, i) => (
            <motion.div
              key={step.num}
              {...fadeUp(i * 0.08)}
              className="grid lg:grid-cols-[56px_1fr] gap-6 lg:gap-10"
            >
              <div className="hidden lg:flex w-[52px] h-[52px] rounded-full bg-earth-green text-cream text-sm font-bold font-heading items-center justify-center shrink-0 ring-4 ring-cream-secondary/50">
                {step.num}
              </div>
              <div className="bg-white rounded-2xl p-7 border border-gray-100 shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <span className="lg:hidden text-xs font-bold text-earth-green font-heading">{step.num}</span>
                  <h4 className="font-heading font-semibold text-sw-text text-lg">{step.title}</h4>
                </div>
                <p className="text-sw-text-secondary text-sm leading-relaxed">{step.body}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ─── 6. TRANSPARENCY & QUALITY ───────────────────────────────────────────── */
const TrustPoints = [
  { icon: ShieldCheck, title: "Controlled kitchen standards", body: "Prepared in a clean, organised kitchen environment with consistent hygiene practices. Every batch is handled with care from start to finish." },
  { icon: Leaf,       title: "No hidden ingredients",   body: "Every ingredient is listed clearly on every jar. If it went in, it is on the label. There are no catch-all preservative categories or vague additives." },
  { icon: Package,    title: "Profile-labelled orders",  body: "Every order is labelled with its customisation settings and smart profile. You will always know what you ordered and why." },
  { icon: Heart,      title: "Honest about limitations", body: "We are a small brand. We do not claim to be everything. We are honest about what we make, how we make it, and what we are still learning." },
];

const TransparencySection = () => (
  <section className="py-24 lg:py-36 bg-cream">
    <div className="section-container">
      <div className="grid lg:grid-cols-2 gap-16 items-center">
        <motion.div {...fadeUp(0)}>
          <Label>Transparency</Label>
          <h2 className="font-heading font-bold text-3xl lg:text-5xl text-sw-text leading-tight mb-6">
            You should know what<br />
            <span className="font-accent italic text-earth-green">you are eating.</span>
          </h2>
          <p className="text-sw-text-secondary text-base lg:text-lg leading-relaxed mb-6">
            Transparency is not a feature of Swadishes. It is the minimum standard
            we hold ourselves to. Food trust is built slowly, through consistent honesty
            Not through claims, certifications, or packaging language.
          </p>
          <p className="text-sw-text-secondary text-base leading-relaxed">
            If something changes in how we source, prepare, or package, we will say so.
            If a batch is not to our standard, it does not go out. That is not exceptional.
            That is just how food should work.
          </p>
        </motion.div>
        <div className="grid sm:grid-cols-2 gap-5">
          {TrustPoints.map((item, i) => (
            <motion.div
              key={item.title}
              {...fadeUp(i * 0.08)}
              className="bg-cream-secondary/70 rounded-2xl p-6 border border-cream-tertiary"
            >
              <div className="w-10 h-10 rounded-xl bg-earth-green/10 flex items-center justify-center mb-4">
                <item.icon size={18} className="text-earth-green" />
              </div>
              <h4 className="font-heading font-semibold text-sw-text text-sm mb-2">{item.title}</h4>
              <p className="text-sw-text-secondary text-xs leading-relaxed">{item.body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ─── 7. SOURCING PHILOSOPHY ──────────────────────────────────────────────── */
const SourcingSection = () => (
  <section className="py-24 lg:py-36 bg-[#2d3a14]">
    <div className="section-container">
      <motion.div {...fadeUp(0)} className="text-center max-w-3xl mx-auto mb-16">
        <Label>
          <span className="text-cream/60">Sourcing</span>
        </Label>
        <h2 className="font-heading font-bold text-3xl lg:text-5xl text-cream leading-tight mb-6">
          Ingredients chosen for<br />
          <span className="font-accent italic text-spice-mustard">how they behave.</span>
        </h2>
        <p className="text-cream/65 text-base lg:text-lg leading-relaxed">
          Sourcing, for Swadishes, is not a procurement exercise. It is part of the recipe.
          The oil you choose changes the texture of the pickle over time. The chilli you
          choose changes how the heat evolves across weeks. These are not incidental choices.
        </p>
      </motion.div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
        {[
          { title: "Local spice knowledge",  body: "We source from spice markets and suppliers that our kitchen team knows, not the cheapest available, but the most consistent and aromatic. Andhra Pradesh's spice culture runs deep, and we work within it respectfully." },
          { title: "Seasonal awareness",     body: "Certain ingredients behave differently across seasons. Raw mango acidity varies. Chilli heat levels shift with the harvest. We account for this rather than pretending ingredients are standardised commodities." },
          { title: "Freshness as a standard",body: "Podis lose aromatic integrity quickly once ground. Pickles need oil of a specific freshness and clarity to cure well. We do not use ingredients past their character window simply because they are technically still usable." },
          { title: "Thoughtful supplier selection", body: "We work with suppliers who can explain their product. Where it came from, how it was stored, what makes one lot different from another. That conversation matters to us." },
          { title: "No compromise on base oils", body: "The oil in an Andhra pickle is not neutral. It is part of the taste. We use sesame and mustard oils chosen for purity and aroma, not for extended shelf life at the cost of character." },
          { title: "Preservation through quality",  body: "The best preservative in a traditional pickle is the quality of its ingredients, the precision of its salt ratio, and the integrity of its oil. We focus there before anything else." },
        ].map((item, i) => (
          <motion.div
            key={item.title}
            {...fadeUp(i * 0.07)}
            className="border border-white/10 rounded-2xl p-7 hover:border-white/20 transition-colors"
          >
            <h4 className="font-heading font-semibold text-cream text-base mb-3">{item.title}</h4>
            <p className="text-cream/55 text-sm leading-relaxed">{item.body}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

/* ─── 8. TRADITION + PERSONALISATION ─────────────────────────────────────── */
const TraditionSection = () => (
  <section className="py-24 lg:py-36 bg-cream-secondary/30">
    <div className="section-container">
      <div className="max-w-4xl mx-auto">
        <motion.div {...fadeUp(0)} className="text-center mb-20">
          <Label>The philosophy</Label>
          <h2 className="font-heading font-bold text-3xl lg:text-5xl text-sw-text leading-tight">
            We are not replacing tradition.<br />
            <span className="font-accent italic text-earth-green">We are honouring how it always worked.</span>
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-10 mb-16">
          <motion.div {...fadeUp(0.1)} className="bg-white rounded-3xl p-10 border border-gray-100 shadow-sm">
            <p className="font-accent italic text-xl text-sw-text leading-relaxed mb-6">
              "In a real home, no one eats the same pickle from the same jar and
              finds it perfect. Someone always asks for it less spicy. Someone
              always wants it tangier. Someone quietly avoids it because of the
              salt. That is not a failure of the recipe. It is the recipe working
              exactly as traditional food should."
            </p>
            <Divider />
            <p className="text-sm text-sw-text-muted">What personalisation actually means</p>
          </motion.div>

          <motion.div {...fadeUp(0.15)} className="space-y-5">
            {[
              { label: "For a child",       adjust: "Less heat, less salt, no whole chillies" },
              { label: "For an elder",      adjust: "Lower sodium, softer texture, easier digestion" },
              { label: "For a heart patient",adjust: "Minimal oil, significantly reduced salt" },
              { label: "For a pregnancy",   adjust: "No hing, careful spice, comfort-first" },
              { label: "For someone who loves heat", adjust: "Full Andhra. Nothing held back." },
            ].map(({ label, adjust }, i) => (
              <motion.div
                key={label}
                {...fadeUp(0.1 + i * 0.07)}
                className="flex items-start gap-4 p-5 bg-white rounded-2xl border border-gray-100"
              >
                <div className="w-2 h-2 rounded-full bg-earth-green mt-1.5 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-sw-text font-heading">{label}</p>
                  <p className="text-xs text-sw-text-muted mt-0.5">{adjust}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>

        <motion.p {...fadeUp(0.2)} className="text-center text-sw-text-secondary text-base lg:text-lg leading-relaxed max-w-2xl mx-auto">
          Swadishes simply brings that same household logic, which has always existed,
          into a format that is accessible, transparent, and available to people who want
          traditional flavours adapted to their real lives.
        </motion.p>
      </div>
    </div>
  </section>
);

/* ─── 9. FINAL STATEMENT ──────────────────────────────────────────────────── */
const FinalStatement = ({ navigate }) => (
  <section className="py-24 lg:py-36 bg-cream">
    <div className="section-container">
      <div className="max-w-3xl mx-auto text-center">
        <motion.div {...fadeUp(0)}>
          <Label>Why we exist</Label>
        </motion.div>
        <motion.h2
          {...fadeUp(0.1)}
          className="font-accent italic text-4xl lg:text-6xl text-sw-text leading-[1.2] mb-8"
        >
          One standard jar<br />
          <span className="text-earth-green">does not work for everyone.</span>
        </motion.h2>
        <motion.p
          {...fadeUp(0.2)}
          className="text-sw-text-secondary text-base lg:text-lg leading-relaxed mb-6"
        >
          Swadishes exists because food is deeply personal, and mass production, by its
          very nature, cannot honour that. We are not trying to build the largest pickle
          brand. We are trying to build the most careful one.
        </motion.p>
        <motion.p
          {...fadeUp(0.25)}
          className="text-sw-text-secondary text-base leading-relaxed mb-14"
        >
          Made with memory. Prepared with care. Adjusted for you.
        </motion.p>
        <motion.div {...fadeUp(0.3)} className="flex flex-wrap gap-4 justify-center">
          <button
            onClick={() => navigate("/studio")}
            className="bg-earth-green text-white rounded-full px-8 py-3.5 font-medium text-sm hover:bg-earth-green-dark transition-all duration-300 shadow-lg shadow-earth-green/20 flex items-center gap-2 group"
          >
            Start Customizing
            <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
          </button>
          <button
            onClick={() => navigate("/")}
            className="border border-earth-green/30 text-earth-green rounded-full px-8 py-3.5 font-medium text-sm hover:bg-earth-green hover:text-white transition-all duration-300"
          >
            Explore Products
          </button>
        </motion.div>
      </div>
    </div>
  </section>
);

/* ─── PAGE ────────────────────────────────────────────────────────────────── */
export default function AboutPage() {
  const navigate = useNavigate();
  return (
    <div className="bg-cream min-h-screen">
      <Navbar />
      <main>
        <AboutHero />
        <BrandEssence />
        <WhereWeFrom />
        <WhoMakes />
        <HowWePrepare />
        <TransparencySection />
        <SourcingSection />
        <TraditionSection />
        <FinalStatement navigate={navigate} />
      </main>
    </div>
  );
}
